import { RequestErrorOptions, isAbortError } from '@adv-frontend/error';
import { resolveOptions, buildUrl, parseJSON, withClientRetry, getResponseFromError } from './utils';
import { AbortError, NetworkError, ParsingError, ReadResponseBodyError, ServerError, TimeoutError } from '../error';
import type {
    ClientRequestInitOptions,
    ClientRequestOptions,
    InferPluginsOptions,
    PluginsOption,
    RequestHeaders,
    FulfilledResponse,
    ResponseType,
    TransformMeta,
    RequestFn,
} from '../types';
import { mocksInterceptor } from '../mocks/interceptor';

/**
 * Создает функцию для выполнения HTTP-запросов на клиенте.
 * Используется для создания функций для работы с конкретными API.
 *
 * @param initOptions - базовые параметры для запросов (базовый url, заголовки и т.п.)
 * @returns функция для выполнения запросов
 */
export function createClientRequest<Plugins extends PluginsOption>(
    initOptions: ClientRequestInitOptions<Plugins> = {}
) {
    return async function request<Result = unknown>(
        rawOptions: ClientRequestOptions<Result, InferPluginsOptions<Plugins>>
    ): Promise<FulfilledResponse<Result>> {
        let options = resolveOptions(rawOptions, initOptions);
        const { transformOptions, onEnd } = options;
        const meta = { options, rawOptions, initOptions };

        options = await transformOptions(options, meta);
        meta.options = options;

        const { body, method, params, signal, url, validateStatus } = options;

        const fullUrl = buildUrl(url, params);
        const errorOptions: RequestErrorOptions<undefined> = {
            // Убираем из сообщения об ошибке query параметры, чтобы правильно работала склейка ошибок в Error Booster
            message: `Failed to ${method} ${url}`,
            request: {
                ...options,
                url: fullUrl,
            },
            requestData: body,
            response: undefined,
            cause: undefined,
            headersWithoutSensitiveData: initOptions.headersWithoutSensitiveData,
            cookiesWithoutSensitiveData: initOptions.cookiesWithoutSensitiveData,
        };

        let response: FulfilledResponse<Result> | undefined;

        try {
            if (signal?.aborted) {
                throw new AbortError(errorOptions);
            }

            response = await getResponse(request, meta, errorOptions);

            if (!validateStatus(response)) {
                throw new ServerError({
                    ...errorOptions,
                    response: {
                        headers: response.headers,
                        status: response.status,
                    },
                    responseData: response.data,
                });
            }
        } catch (err) {
            onEnd(response ?? getResponseFromError(err) ?? {}, err, meta);
            throw err;
        }

        // onEnd нужно вызывать только один раз, поэтому он за пределами try-catch
        onEnd(response ?? {}, undefined, meta);
        return response;
    };

    async function getResponse<Result>(
        request: RequestFn<InferPluginsOptions<Plugins>>,
        meta: TransformMeta<Result, InferPluginsOptions<Plugins>>,
        errorOptions: RequestErrorOptions<undefined>
    ) {
        const mockPromise = mocksInterceptor<Result>(request, meta.rawOptions, meta.options);
        if (mockPromise) {
            return mockPromise;
        }

        return doRequest(meta, errorOptions);
    }

    async function doRequest<Result>(
        meta: TransformMeta<Result, InferPluginsOptions<Plugins>>,
        errorOptions: RequestErrorOptions<undefined>
    ) {
        const { method, responseType, retry, signal, timeout, transformResponse, transformResult } = meta.options;

        const abortController = getAbortController(signal);

        const fetchResponse = await withClientRetry(
            () => withTimeout(doFetch(meta, errorOptions, abortController), abortController, errorOptions, timeout),
            method,
            retry
        );

        const responseHeaders = Object.fromEntries(fetchResponse.headers);

        let responseData = await getResponseData<Result>(fetchResponse, responseType, {
            ...errorOptions,
            response: {
                headers: responseHeaders,
                status: fetchResponse.status,
            },
        });

        responseData = await transformResult(responseData, meta);

        const response: FulfilledResponse<Result> = {
            data: responseData,
            headers: responseHeaders,
            status: fetchResponse.status,
        };

        return transformResponse(response, meta);
    }

    async function doFetch<Result>(
        meta: TransformMeta<Result, InferPluginsOptions<Plugins>>,
        errorOptions: RequestErrorOptions<undefined>,
        abortController: AbortController
    ) {
        const { body, cache, credentials, headers = {}, keepalive, method, mode, params, redirect, url } = meta.options;

        const finalUrl = buildUrl(url, params);
        const requestInit: RequestInit = {
            body,
            cache,
            credentials,
            headers: createFetchHeaders(headers),
            keepalive,
            method,
            mode,
            redirect,
            signal: abortController.signal,
        };

        try {
            return await fetch(finalUrl, requestInit);
        } catch (error) {
            if (isAbortError(error)) {
                throw new AbortError({
                    ...errorOptions,
                    cause: error,
                });
            }

            throw new NetworkError({
                ...errorOptions,
                cause: error,
            });
        }
    }
}

function createFetchHeaders(requestHeaders: RequestHeaders) {
    return Object.entries(requestHeaders).reduce((headers, [header, value]) => {
        if (Array.isArray(value)) {
            value.forEach(valueItem => headers.append(header, valueItem));
        } else if (typeof value !== 'undefined') {
            headers.append(header, value);
        }

        return headers;
    }, new Headers());
}

function withTimeout<ResponsePromise extends Promise<unknown>>(
    promise: ResponsePromise,
    abortController: AbortController,
    requestErrorOptions: RequestErrorOptions<undefined>,
    timeoutOption?: { request?: number } | number
): ResponsePromise {
    const timeout = typeof timeoutOption === 'number' ? timeoutOption : timeoutOption?.request;

    if (!timeout) {
        return promise;
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    return Promise.race([
        promise,
        new Promise((_resolve, reject) => {
            setTimeout(() => {
                reject(new TimeoutError(requestErrorOptions));
                abortController.abort();
            }, timeout);
        }),
    ]) as ResponsePromise;
}

function getAbortController(signal?: AbortSignal) {
    const abortController = new AbortController();

    if (signal) {
        if (signal.aborted) {
            abortController.abort();
        } else {
            signal.addEventListener('abort', () => abortController.abort());
        }
    }

    return abortController;
}

async function getResponseData<T>(
    fetchResponse: Response,
    responseType: ResponseType,
    requestErrorOptions: RequestErrorOptions<undefined>
): Promise<T> {
    if (responseType === 'blob') {
        try {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
            return fetchResponse.blob() as T;
        } catch (error) {
            throw new ReadResponseBodyError({
                ...requestErrorOptions,
                cause: error,
            });
        }
    }

    let body;
    try {
        body = await fetchResponse.text();
    } catch (error) {
        throw new ReadResponseBodyError({
            ...requestErrorOptions,
            cause: error,
        });
    }

    if (responseType === 'json') {
        try {
            return parseJSON<T>(body);
        } catch (error) {
            throw new ParsingError({
                ...requestErrorOptions,
                responseData: body,
                cause: error,
            });
        }
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    return body as T;
}
